import { useCallback, useMemo, useState } from "react";
import {
  HydratedChatbot,
  useChatbotConnectedAssets,
  useChatbotAccountMembers,
  useChangeChatbotOwner,
  Member,
} from "@zapier/client";
import { ChangeOwnerModal, ChangeOwnerUser } from "@zapier/shared";
import { AssetTypes, useNotification } from "@zapier/utils";
import { useEmitChatbotChangeOwnerModalEvent } from "@zapier/shared/src/events";
import { useTranslations } from "@zapier/i18n";

interface Props {
  chatbot?: HydratedChatbot;
  isOpen: boolean;
  onCloseModal: () => void;
  preselectedUser?: Member;
  folderId?: string; // used for cache invalidation
}

const ChatbotChangeOwnerModal = ({
  chatbot,
  isOpen,
  onCloseModal,
  preselectedUser,
  folderId,
}: Props) => {
  const { createNotification } = useNotification();
  const { emit } = useEmitChatbotChangeOwnerModalEvent();
  const t = useTranslations("chatbot-change-owner-modal");

  const {
    transformedAssets,
    isLoading: isLoadingAssets,
    isFetching: isFetchingAssets,
  } = useChatbotConnectedAssets(isOpen ? chatbot?.id : undefined);

  const [userSearch, setUserSearch] = useState("");

  const { users: chatbotAccountMembers, isLoading: isUsersLoading } =
    useChatbotAccountMembers({
      ownerId: chatbot?.ownerCustomuserId,
      userSearch,
      enabled: isOpen,
    });

  const users: ChangeOwnerUser[] = useMemo(() => {
    return chatbotAccountMembers.map((member) => {
      const { chatbotsCreatorId, id, name, email, avatarUrl } = member;
      return {
        id,
        name,
        email,
        photoUrl: avatarUrl,
        isDisabled: !chatbotsCreatorId,
      };
    });
  }, [chatbotAccountMembers]);

  const { changeChatbotOwner, isSuccess, isError } = useChangeChatbotOwner({
    folderId,
  });

  const onSearch = useCallback((search: string) => {
    setUserSearch(search);
  }, []);

  if (!chatbot || !isOpen) {
    return;
  }

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      emit({ optionSelected: "cancel", chatbotId: chatbot.id });
      onCloseModal();
    }
  };
  const onSubmit = async (ownerId: string) => {
    const customuserId = preselectedUser?.id || ownerId;
    const user = chatbotAccountMembers.find((user) => user.id === customuserId);

    if (customuserId) {
      emit({ optionSelected: "confirm", chatbotId: chatbot.id });
      await changeChatbotOwner(
        {
          chatbotId: chatbot.id || "",
          newOwnerId: user?.chatbotsCreatorId || "",
        },
        {
          onSuccess: () => {
            createNotification({
              message: t("notifications.owner-changed-success"),
              type: "success",
            });
          },

          onError: (error) => {
            createNotification({
              message: t("notifications.owner-changed-error", {
                errorMessage: error.message,
              }),
              type: "error",
            });
          },
        },
      );

      if (isSuccess) {
        emit({
          eventAction: "view",
          optionSelected: "success",
          chatbotId: chatbot.id,
        });
      }

      if (isError) {
        emit({
          eventAction: "view",
          optionSelected: "error",
          chatbotId: chatbot.id,
        });

        setUserSearch("");
      }
    }
  };

  return (
    <ChangeOwnerModal
      users={users}
      id={chatbot.id ?? ""}
      title={chatbot.name ?? ""}
      currentOwnerId={chatbot.owner?.id}
      isOpen={isOpen}
      setIsOpen={handleOpenChange}
      linkedAssets={transformedAssets}
      assetType={AssetTypes.Chatbot}
      isLoading={isLoadingAssets || isFetchingAssets}
      onSearch={onSearch}
      isUsersLoading={isUsersLoading}
      onSubmit={onSubmit}
      preselectedUser={preselectedUser}
    />
  );
};

export default ChatbotChangeOwnerModal;
